import React, { useState, useEffect } from 'react';
import { kasaBankService } from '../../services/kasaBankService';
import BaseModal from '../common/BaseModal';

function AddOperationModal({ isOpen, onClose, onSuccess, operationType = 'KP', locationId = null }) {
  const [formData, setFormData] = useState({
    typ_operacji: operationType,
    typ_platnosci: 'gotowka',
    kwota: '',
    opis: '',
    kategoria: 'inne',
    // Numer KP/KW nadaje system z prefiksow - w formularzu podajemy wylacznie
    // numer dokumentu OBCEGO (np. faktury kontrahenta)
    numer_dokumentu_obcy: '',
    kontrahent: '',
    data_operacji: new Date().toISOString().split('T')[0],
    uwagi: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Opcje typow platnosci
  const paymentTypes = [
    { value: 'gotowka', label: 'Gotowka', icon: 'fas fa-money-bill' },
    { value: 'karta', label: 'Karta', icon: 'fas fa-credit-card' },
    { value: 'blik', label: 'BLIK', icon: 'fas fa-mobile-alt' },
    { value: 'przelew', label: 'Przelew', icon: 'fas fa-university' },
    { value: 'kupon', label: 'Kupon', icon: 'fas fa-ticket-alt' }
  ];

  // Kategorie dla roznych typow operacji
  const kategorieKP = [
    { value: 'sprzedaz', label: 'Sprzedaz' },
    { value: 'zwrot', label: 'Zwrot' },
    { value: 'kupony', label: 'Kupony' },
    { value: 'inne_przychody', label: 'Inne przychody' },
    { value: 'inne', label: 'Inne' }
  ];

  const kategorieKW = [
    { value: 'zakupy', label: 'Zakupy' },
    { value: 'materialy', label: 'Materialy biurowe' },
    { value: 'dostawa', label: 'Dostawa' },
    { value: 'oplaty', label: 'Oplaty' },
    { value: 'wynagrodzenia', label: 'Wynagrodzenia' },
    { value: 'inne_wydatki', label: 'Inne wydatki' },
    { value: 'inne', label: 'Inne' }
  ];

  // Reset formularza przy otwarciu
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        typ_operacji: operationType,
        data_operacji: new Date().toISOString().split('T')[0]
      }));
      setError('');
    }
  }, [isOpen, operationType]);

  const getCurrentKategorie = () => {
    return formData.typ_operacji === 'KP' ? kategorieKP : kategorieKW;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.kwota || parseFloat(formData.kwota) <= 0) {
        throw new Error('Kwota musi byc wieksza od 0');
      }

      if (!formData.opis.trim()) {
        throw new Error('Opis jest wymagany');
      }

      const operacjaData = {
        ...formData,
        kwota: parseFloat(formData.kwota),
        location_id: locationId
      };

      await kasaBankService.createOperacja(operacjaData);

      onSuccess && onSuccess();
      onClose();

      // Reset formularza
      setFormData({
        typ_operacji: operationType,
        typ_platnosci: 'gotowka',
        kwota: '',
        opis: '',
        kategoria: 'inne',
        numer_dokumentu_obcy: '',
        kontrahent: '',
        data_operacji: new Date().toISOString().split('T')[0],
        uwagi: ''
      });
    } catch (err) {
      setError(err.message || 'Blad podczas dodawania operacji');
    } finally {
      setLoading(false);
    }
  };

  const isKP = formData.typ_operacji === 'KP';

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isKP ? 'Dodaj wplyw (KP)' : 'Dodaj wydatek (KW)'}
      subtitle={isKP ? 'Kasa Przyjmie - wplata do kasy' : 'Kasa Wydaje - wyplata z kasy'}
      headerIcon={isKP ? '📥' : '📤'}
      size="md"
      headerStyle={{
        backgroundColor: isKP ? 'var(--success)' : 'var(--danger)',
        color: '#fff' ,
        borderRadius: '0.375rem 0.375rem 0 0',
      }}
    >
      <form onSubmit={handleSubmit}>

        {/* Sekcja glowna */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '5px',
          border: '1px solid var(--border)'
        }}>
          <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
            💰 Dane operacji
          </h6>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
                Typ operacji
              </label>
              <select
                value={formData.typ_operacji}
                onChange={(e) => handleChange('typ_operacji', e.target.value)}
                style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
              >
                <option value="KP">KP - Wplyw</option>
                <option value="KW">KW - Wydatek</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
                Typ platnosci
              </label>
              <select
                value={formData.typ_platnosci}
                onChange={(e) => handleChange('typ_platnosci', e.target.value)}
                style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
              >
                {paymentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
                Kwota (zl) *
              </label>
              <input
                type="number" step="0.01" min="0" value={formData.kwota}
                onChange={(e) => handleChange('kwota', e.target.value)} placeholder="0.00" required
                style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px', fontWeight: '600' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
                Data operacji
              </label>
              <input
                type="date" value={formData.data_operacji}
                onChange={(e) => handleChange('data_operacji', e.target.value)}
                style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
              />
            </div>
          </div>
        </div>

        {/* Sekcja opisu */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '5px',
          border: '1px solid var(--border)'
        }}>
          <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
            📝 Opis operacji
          </h6>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
              Opis *
            </label>
            <input
              type="text" value={formData.opis}
              onChange={(e) => handleChange('opis', e.target.value)}
              placeholder={isKP ? 'np. Sprzedaz produktow' : 'np. Zakup materialow biurowych'} required
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
                Kategoria
              </label>
              <select
                value={formData.kategoria}
                onChange={(e) => handleChange('kategoria', e.target.value)}
                style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
              >
                {getCurrentKategorie().map(kat => (
                  <option key={kat.value} value={kat.value}>{kat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
                Nr dokumentu obcego
              </label>
              <input
                type="text" value={formData.numer_dokumentu_obcy}
                onChange={(e) => handleChange('numer_dokumentu_obcy', e.target.value)} placeholder="np. FV001/2025"
                style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
              />
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>
                Numer {isKP ? 'KP' : 'KW'} nadaje system po zapisaniu. Tutaj wpisz numer dokumentu kontrahenta.
              </div>
            </div>
          </div>
        </div>

        {/* Sekcja kontrahenta */}
        <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem',
          background: 'var(--bg)',
          borderRadius: '5px',
          border: '1px solid var(--border)'
        }}>
          <h6 style={{ margin: '0 0 0.75rem 0', fontWeight: '600', color: 'var(--text-secondary)', fontSize: '12px' }}>
            👤 Kontrahent i uwagi
          </h6>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
              Kontrahent
            </label>
            <input
              type="text" value={formData.kontrahent}
              onChange={(e) => handleChange('kontrahent', e.target.value)}
              placeholder={isKP ? 'np. Jan Kowalski' : 'np. Firma XYZ Sp. z o.o.'}
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', marginBottom: '3px' }}>
              Uwagi
            </label>
            <textarea
              value={formData.uwagi}
              onChange={(e) => handleChange('uwagi', e.target.value)} rows="2"
              placeholder="Dodatkowe informacje..."
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '12px', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Blad */}
        {error && (
          <div style={{
            color: 'var(--danger)', background: 'var(--danger-light)', border: '1px solid var(--danger-light)',
            borderRadius: '4px', padding: '0.5rem 0.75rem', marginBottom: '0.75rem', fontSize: '11px'
          }}>
            <i className="fas fa-exclamation-circle" style={{ marginRight: '0.5rem' }}></i>
            {error}
          </div>
        )}

        {/* Przyciski */}
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            type="button" onClick={onClose}
            style={{
              padding: '6px 12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' ,
              fontWeight: '500', fontSize: '12px', cursor: 'pointer'
            }}
          >
            Anuluj
          </button>
          <button
            type="submit" disabled={loading}
            style={{
              padding: '6px 12px',
              background: loading ? 'var(--info-light)' : (isKP ? 'var(--success)' : 'var(--danger)'),
              color: '#fff' ,
              fontWeight: '600', fontSize: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Zapisywanie...' : (isKP ? '📥 Dodaj wplyw' : '📤 Dodaj wydatek')}
          </button>
        </div>
      </form>
    </BaseModal>
  );
}

export default AddOperationModal;
